import { Close } from "@mui/icons-material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import EditDocumentIcon from "@mui/icons-material/EditNote";
import { Divider, ListItemIcon } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import I18n from "i18n";
import { FC, MouseEvent, useState } from "react";
import { EditMode, useEventEditorContext } from "./EventEditorContext";

// Only three modes are available in this UX flow
const availableEditModes: Exclude<EditMode, null>[] = [
  "dragDropUpdate",
  "singleEdit",
  "groupEdit",
];

// Mapping edit modes to their corresponding icons.
const iconForMode: { [key in Exclude<EditMode, null>]: JSX.Element } = {
  dragDropUpdate: <DragIndicatorIcon />,
  singleEdit: <EditIcon />,
  groupEdit: <EditDocumentIcon />,
};

const EditButton: FC = () => {
  const { editMode, setEditMode } = useEventEditorContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectMode = (mode: Exclude<EditMode, null>) => {
    setEditMode(mode);
    handleCloseMenu();
  };

  const handleCancelEdit = () => {
    setEditMode(null);
    handleCloseMenu();
  };

  return (
    <>
      <Tooltip
        title={
          editMode
            ? I18n.t("EventEditor.changeEditMode")
            : I18n.t("EventEditor.selectEditMode")
        }
      >
        <IconButton
          onClick={handleOpenMenu}
          color={editMode ? "info" : undefined}
        >
          {editMode ? iconForMode[editMode] : <EditIcon />}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {availableEditModes.map(mode => (
          <MenuItem
            key={mode}
            onClick={
              editMode === mode ? undefined : () => handleSelectMode(mode)
            }
            sx={{ pointerEvents: editMode === mode ? "none" : "auto" }}
            selected={editMode === mode}
          >
            <ListItemIcon>{iconForMode[mode]}</ListItemIcon>
            {I18n.t(`EventEditor.editModes.${mode}.label`)}
          </MenuItem>
        ))}
        {editMode && <Divider />}
        {editMode && (
          <MenuItem onClick={handleCancelEdit}>
            <ListItemIcon>
              <Close />
            </ListItemIcon>
            {I18n.t("EventEditor.cancelEditMode")}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default EditButton;
